import type { WorkspaceTabDescriptor, WorkspaceTabType } from 'src/models/workspaceTabs';

interface WorkspaceRouteConfiguration {
	closable: boolean;
	icon: string;
	type: WorkspaceTabType;
}

const WORKSPACE_ROUTE_CONFIGURATIONS: Record<string, WorkspaceRouteConfiguration> = {
	PhoneBook: { closable: true, icon: 'fa-book', type: 'module' },
	addon: { closable: true, icon: 'fa-bolt', type: 'object' },
	agendaList: { closable: true, icon: 'fa-calendar-days', type: 'module' },
	appointment: { closable: true, icon: 'fa-calendar-days', type: 'object' },
	document: { closable: true, icon: 'fa-file-lines', type: 'object' },
	documentList: { closable: true, icon: 'fa-file-lines', type: 'module' },
	filterList: { closable: true, icon: 'fa-filter', type: 'module' },
	inbox: { closable: false, icon: 'fa-home', type: 'module' },
	kanbanBoard: { closable: true, icon: 'fa-layer-group', type: 'module' },
	mail: { closable: true, icon: 'fa-envelope', type: 'object' },
	mailList: { closable: true, icon: 'fa-envelope', type: 'module' },
	mailSend: { closable: true, icon: 'fa-envelope', type: 'object' },
	mailSendWithReceiver: { closable: true, icon: 'fa-envelope', type: 'object' },
	memo: { closable: true, icon: 'fa-clipboard', type: 'object' },
	message: { closable: true, icon: 'fa-comment-alt', type: 'object' },
	messageList: { closable: true, icon: 'fa-comment-alt', type: 'module' },
	note: { closable: true, icon: 'fa-pen-to-square', type: 'object' },
	noteList: { closable: true, icon: 'fa-pen-to-square', type: 'module' },
	notebookList: { closable: true, icon: 'fa-folder', type: 'module' },
	organization: { closable: true, icon: 'fa-sitemap', type: 'module' },
	phoneCall: { closable: true, icon: 'fa-phone', type: 'object' },
	phoneCallCreate: { closable: true, icon: 'fa-phone', type: 'object' },
	phoneCallList: { closable: true, icon: 'fa-phone', type: 'module' },
	record: { closable: true, icon: 'fa-notebook', type: 'object' },
	records: { closable: true, icon: 'fa-notebook', type: 'module' },
	relationsHistory: { closable: true, icon: 'fa-history', type: 'object' },
	settings: { closable: true, icon: 'fa-gear', type: 'module' },
	task: { closable: true, icon: 'fa-square-check', type: 'object' },
	taskList: { closable: true, icon: 'fa-square-check', type: 'module' },
	timer: { closable: true, icon: 'fa-stopwatch', type: 'module' },
	trashList: { closable: true, icon: 'fa-trash', type: 'module' },
	userOnline: { closable: true, icon: 'fa-users', type: 'module' },
};

const OBJECT_TAB_ID_SEPARATOR = ':';
const LIST_INSTANCE_TAB_ID_INFIX = ':list:';
const CONTACT_SEARCH_TAB_ID_INFIX = ':contactSearch:';
const RECORDS_ROUTE_NAME = 'records';
const RECORDS_INSTANCE_TAB_ID_INFIX = ':instance:';
const RECORDS_QUERY_TAB_ID_INFIX = ':query:';
const MAIL_SEND_ROUTE_NAME = 'mailSend';
const MAIL_SEND_WITH_RECEIVER_ROUTE_NAME = 'mailSendWithReceiver';
const COMPOSE_DRAFT_TAB_ID_INFIX = ':draft:';
const COMPOSE_IDENTITY_TAB_ID_INFIX = ':compose:';
const COMPOSE_ATTACHMENT_TAB_ID_INFIX = ':attachment:';
const COMPOSE_DEFAULT_REPLY_TYPE = 'reply';
const PHONE_CALL_CREATE_ROUTE_NAME = 'phoneCallCreate';
const PHONE_CALL_CREATE_NUMBER_TAB_ID_INFIX = ':number:';

export const ADDON_ROUTE_NAME = 'addon';
export const RECORDS_SEARCH_TAB_ID = 'records:search';
export const RECORDS_SEARCH_MARKER_QUERY_KEY = 'searchTab';

type StringifiableRouteValue = Array<boolean | number | string> | boolean | number | string;

const stringifyParams = (params: Record<string, unknown>): Record<string, string> => {
	const stringified: Record<string, string> = {};
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== null) {
			stringified[key] = String(value as StringifiableRouteValue);
		}
	}
	return stringified;
};

// `?list=<sharedQueryId>` macht eine gespeicherte Liste zu ihrem eigenen, schließbaren Modul-Tab —
// die Query bleibt Teil der Tab-Identität, sonst kollabieren alle Listen-Instanzen auf denselben Tab.
const listQueryValue = (query: Record<string, unknown> | undefined): null | string => {
	const rawValue = query?.list;
	if (rawValue === undefined || rawValue === null || rawValue === '') {
		return null;
	}

	return String(rawValue as StringifiableRouteValue);
};

const contactSearchQueryValue = (query: Record<string, unknown> | undefined): null | string => {
	const rawValue = query?.contactSearch;
	if (rawValue === undefined || rawValue === null || rawValue === '') {
		return null;
	}

	return String(rawValue as StringifiableRouteValue);
};

const nonEmptyRouteValue = (value: unknown): null | string => {
	if (value === undefined || value === null) {
		return null;
	}
	const stringValue = String(value as StringifiableRouteValue).trim();
	return stringValue === '' ? null : stringValue;
};

// Die Akten-Route trägt Instanz und gespeicherte Liste als Pfadsegmente
// (`/records/:instanceId?/:sharedQueryId?`). Beide gehören zur Tab-Identität, sonst kollabieren alle
// geöffneten Akten auf denselben Reiter. Die virtuelle Instanz 'all' ist die Übersicht und behält
// bewusst die nackte Route-ID. Der Suchbegriff (`?search_<id>=`) ist NICHT Teil der Identität —
// eine neue Suche soll denselben Akten-Reiter weiterverwenden statt einen zweiten zu öffnen.
const recordsTabIdSuffix = (params: Record<string, unknown>): string => {
	const instanceId = nonEmptyRouteValue(params.instanceId);
	if (instanceId === null || instanceId === 'all') {
		return '';
	}

	const sharedQueryId = nonEmptyRouteValue(params.sharedQueryId);
	if (sharedQueryId === null) {
		return `${RECORDS_INSTANCE_TAB_ID_INFIX}${instanceId}`;
	}

	return `${RECORDS_INSTANCE_TAB_ID_INFIX}${instanceId}${RECORDS_QUERY_TAB_ID_INFIX}${sharedQueryId}`;
};

// `?searchTab=1` macht den Reiter zum geteilten Suche-Tab (ExtJS-Parität): EINE stabile Identität
// unabhängig von der Instanz, damit jeder Treffer-Klick denselben Reiter wiederverwendet statt einen
// Instanz-Tab zu öffnen. Nur mit konkreter Instanz gültig — ohne sie greift die normale Ableitung.
// Eine Route mit gespeicherter Auswertung (`sharedQueryId`) ist NIE der Suche-Tab, auch wenn der Marker
// mitgetragen wird — sonst kapert das Öffnen einer Auswertung während aktivem Suche-Tab dessen Identität.
const isRecordsSearchRoute = (params: Record<string, unknown>, query: Record<string, unknown> | undefined): boolean => {
	const marker = query?.[RECORDS_SEARCH_MARKER_QUERY_KEY];
	if (marker === undefined || marker === null || marker === '') {
		return false;
	}
	if (nonEmptyRouteValue(params.sharedQueryId) !== null) {
		return false;
	}
	const instanceId = nonEmptyRouteValue(params.instanceId);
	return instanceId !== null && instanceId !== 'all';
};

const composeTabIdSuffix = (
	routeName: string,
	params: Record<string, unknown>,
	query: Record<string, unknown> | undefined
): string => {
	if (routeName === MAIL_SEND_WITH_RECEIVER_ROUTE_NAME) {
		const receiverAddressTo = nonEmptyRouteValue(params.receiverAddressTo);
		return receiverAddressTo === null ? '' : `${OBJECT_TAB_ID_SEPARATOR}${receiverAddressTo}`;
	}

	if (routeName === PHONE_CALL_CREATE_ROUTE_NAME) {
		const desiredPhoneNumberToContact = nonEmptyRouteValue(params.desiredPhoneNumberToContact);
		if (desiredPhoneNumberToContact === null) {
			return '';
		}
		const idValue = nonEmptyRouteValue(params.id);
		const idSuffix = idValue === null ? '' : `${OBJECT_TAB_ID_SEPARATOR}${idValue}`;
		return `${idSuffix}${PHONE_CALL_CREATE_NUMBER_TAB_ID_INFIX}${desiredPhoneNumberToContact}`;
	}

	if (routeName !== MAIL_SEND_ROUTE_NAME) {
		return '';
	}

	const replyOrForwardId = nonEmptyRouteValue(params.replyOrForwardId);
	if (replyOrForwardId !== null) {
		const replyOrForwardType = nonEmptyRouteValue(params.replyOrForwardType) ?? COMPOSE_DEFAULT_REPLY_TYPE;
		return `${OBJECT_TAB_ID_SEPARATOR}${replyOrForwardType}${OBJECT_TAB_ID_SEPARATOR}${replyOrForwardId}`;
	}

	const draftIdToLoad = nonEmptyRouteValue(params.draftIdToLoad);
	if (draftIdToLoad !== null) {
		return `${COMPOSE_DRAFT_TAB_ID_INFIX}${draftIdToLoad}`;
	}

	const composeIdentity = nonEmptyRouteValue(query?.composeIdentity);
	if (composeIdentity !== null) {
		return `${COMPOSE_IDENTITY_TAB_ID_INFIX}${composeIdentity}`;
	}

	const initialAttachmentToken = nonEmptyRouteValue(query?.initialAttachmentToken);
	return initialAttachmentToken === null ? '' : `${COMPOSE_ATTACHMENT_TAB_ID_INFIX}${initialAttachmentToken}`;
};

export const buildWorkspaceTabId = (
	routeName: string,
	params: Record<string, unknown>,
	query?: Record<string, unknown>
): string => {
	const configuration = WORKSPACE_ROUTE_CONFIGURATIONS[routeName];
	if (configuration === undefined || configuration.type === 'module') {
		if (routeName === RECORDS_ROUTE_NAME) {
			if (isRecordsSearchRoute(params, query)) {
				return RECORDS_SEARCH_TAB_ID;
			}
			return `${routeName}${recordsTabIdSuffix(params)}`;
		}
		const listInstanceId = listQueryValue(query);
		if (listInstanceId !== null) {
			return `${routeName}${LIST_INSTANCE_TAB_ID_INFIX}${listInstanceId}`;
		}
		const contactSearchTerm = contactSearchQueryValue(query);
		return contactSearchTerm === null
			? routeName
			: `${routeName}${CONTACT_SEARCH_TAB_ID_INFIX}${contactSearchTerm}`;
	}

	const composeSuffix = composeTabIdSuffix(routeName, params, query);
	if (composeSuffix !== '') {
		return `${routeName}${composeSuffix}`;
	}

	// Objekt-Routen ohne :id-Param und ohne Compose-Wert sind Single-Instance —
	// sonst kollabieren alle Instanzen auf dieselbe leere ID (Review-Blocker).
	const idParam = params.id;
	if (idParam === undefined || idParam === null) {
		return routeName;
	}

	const idString = typeof idParam === 'string' ? idParam : String(idParam as boolean | number);
	if (idString === '') {
		return routeName;
	}

	return `${routeName}${OBJECT_TAB_ID_SEPARATOR}${idString}`;
};

export const describeRouteAsWorkspaceTab = (route: {
	name?: unknown;
	params: Record<string, unknown>;
	query: Record<string, unknown>;
}): WorkspaceTabDescriptor | null => {
	if (typeof route.name !== 'string') {
		return null;
	}

	const configuration = WORKSPACE_ROUTE_CONFIGURATIONS[route.name];
	if (configuration === undefined) {
		return null;
	}

	const listInstanceId = configuration.type === 'module' ? listQueryValue(route.query) : null;
	const contactSearchTerm =
		configuration.type === 'module' && listInstanceId === null ? contactSearchQueryValue(route.query) : null;
	const id = buildWorkspaceTabId(route.name, route.params, route.query);
	const isRecordsSearchTab = id === RECORDS_SEARCH_TAB_ID;

	return {
		// `?list=` erzwingt closable — sonst erbt eine gespeicherte Liste auf `inbox` dessen closable:false.
		closable: listInstanceId === null && contactSearchTerm === null ? configuration.closable : true,
		icon: isRecordsSearchTab
			? 'fa-magnifying-glass'
			: contactSearchTerm === null
				? configuration.icon
				: 'fa-clock-rotate-left',
		id,
		params: stringifyParams(route.params),
		query: stringifyParams(route.query),
		routeName: route.name,
		titleKey: isRecordsSearchTab
			? 'workspace_tabs_title_records_search'
			: contactSearchTerm === null
				? `workspace_tabs_title_${route.name}`
				: 'workspace_tabs_title_contact_history',
		type: configuration.type,
	};
};
